// Copyright (c) 2019 by Michael Marucheck
//
// Use of this source code is governed by an MIT-style license that
// can be found in the LICENSE file distributed with this file.

import uuidv4 from "uuid/v4";
import React, { useState, useEffect } from "react";
import { RouteComponentProps, Link, withRouter } from "react-router-dom";
import Markdown from "react-markdown/with-html";

import { getApi, volCache } from "../store/api";
import { throwIfNull, todayString } from "../util";
import { logIfErrorA } from "../util";
import { update, useWidthGate } from "../UiUtil";
import { breadcrumbs, URLS } from "../urls";
import { NavBar, navLink } from "../components/NavBar";
import { PageContainer } from "../components/PageContainer";

const WIDTH_GATE = {
  wideNav: 501,
};

const Page: React.FC<RouteComponentProps<{ uid: string }>> = props => {
  const { history, match } = props;

  debug("======= MarkdownPage =======");
  const uid = match.params.uid;
  const [state, setState] = useState(volCache().get());
  const widthBucket = useWidthGate(WIDTH_GATE);

  const { markdowns, school, user, volunteer: vDoc } = state;
  const volunteer = vDoc && vDoc.data;
  const markdown = markdowns.find(x => x.data.uid === uid);
  const loaded = state.loaded && markdown != null;

  const initAcceptDate = getFirst(
    item => item && item.date,
    volunteer && volunteer.acceptedMarkdowns.filter(x => x.markdownUid === uid)
  );

  const [acceptDate, setAcceptDate] = useState<string | null>(initAcceptDate);

  const api = getApi();

  useEffect(() =>
    volCache().subscribe(newState => {
      if (update("setState", state, setState, newState)) {
        if (acceptDate == null && newState.volunteer != null) {
          const newVol = newState.volunteer.data;
          const acceptance = newVol.acceptedMarkdowns.find(
            x => x.markdownUid === uid
          );
          if (acceptance != null) {
            setAcceptDate(acceptance.date);
          }
        }
      }
    })
  );

  async function accept() {
    if (!loaded || volunteer == null) {
      debug(`ACCEPTED BUT NOT YET LOADED.  No-op`);
      return;
    }

    const today = todayString();
    setAcceptDate(today);
    logIfErrorA(async () => {
      const volunteerUid = throwIfNull(user).uid;
      const updated = {
        ...volunteer,
        acceptedMarkdowns: [
          ...volunteer.acceptedMarkdowns.filter(x => x.markdownUid !== uid),
          {
            uid: `${uid}-${volunteerUid}`,
            markdownUid: uid,
            volunteerUid,
            date: today,
          },
        ],
      };
      await api.volunteer.saveVolunteer(updated);
      history.push(URLS.vHome());
    });
  }

  return (
    <PageContainer
      title={markdown ? markdown.data.title : ""}
      crumbs={breadcrumbs().vHome}
      administrators={state.administrators}
      school={state.school}
      checks={{ user, loaded: loaded && markdown != null }}
    >
      {user &&
        loaded &&
        markdown &&
        (volunteer == null || school == null ? (
          <div className="section">
            <Link to={URLS.vProfile()}>Click here</Link> to choose your school
            and then you can come back here to read the guidelines.
          </div>
        ) : (
            <>
              <div className="section">
                Please read the following document and acknowledge at the bottom
                of this page.
            </div>
              <div className="section">
                <Markdown
                  source={atob(throwIfNull(markdown).data.contents)}
                  escapeHtml={false}
                />
              </div>
              <div className="section">
                {acceptDate != null ? (
                  `Acknowledged on ${acceptDate}`
                ) : (
                    <button type="button" onClick={accept}>
                      I acknowledge that I have read this document.
                </button>
                  )}
              </div>
              {acceptDate != null && (
                <div>
                  <div className="nav">
                    Here are some ideas for where to go next:
                </div>
                  <NavBar
                    wide={widthBucket >= WIDTH_GATE.wideNav}
                    links={[
                      navLink(URLS.index(), "Home"),
                      navLink(URLS.vProfile(), "Edit Profile"),
                      navLink(
                        URLS.vActivitiesDetail(uuidv4()),
                        "Enter New Activity"
                      ),
                    ]}
                  />
                </div>
              )}
            </>
          ))}
    </PageContainer>
  );
};
export const MarkdownPage = withRouter(Page);

function getFirst<Obj, Field>(
  get: (item: Obj | null) => Field,
  array: Obj[] | null
): Field {
  return array && array.length > 0 ? get(array[0]) : get(null);
}

function debug(message: string) {
  if (true) {
    console.log(`MarkdownPage: ${message}`);
  }
}
